import React, {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {PathUtility} from 'src/shared/constants/PathBuilder';

import {useAlert} from 'src/shared/hooks/useAlert';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {capitalize} from 'src/shared/utils/formatters';

import {Stack} from 'src/design/atoms/Stack';
import {useDisclosure} from '@mantine/hooks';
import type {TaskDefinitionResponse} from 'src/api/ProactApiClient';
import {
  AttachmentModuleEnum,
  EntityIdentifierEnum,
  FeatureFlagEnum,
  ModuleEnum,
  NoteModuleEnum,
  NotificationModuleEnum,
  PermissionOperationEnum,
  TaskDefinitionStateEnum,
  TaskTypeEnum,
} from 'src/api/ProactApiClient';
import {
  useDeleteTaskDefinitionMutation,
  useLazyGetTaskDefinitionDetailsQuery,
} from 'src/redux/queries/taskDefinition';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {SplitPane} from 'src/design/atoms/SplitPane';
import {AttachmentsModule} from 'src/app/attachments/AttachmentsModule';
import {AuditModule} from 'src/app/audit-logs/AuditModule';
import {LockGuard} from 'src/app/lock-mgt/LockGuard';
import {NotesModule} from 'src/app/notes/NotesModule';
import {NotificationsModule} from 'src/app/notifications/NotificationsModule';
import {AdminTaskAssignmentsListByTask} from 'src/app/tasks/AdminTaskAssignmentsListByTask';
import {
  GetUrlSectionFromTaskType,
  TaskPillConfig,
  taskRowSubText,
} from 'src/app/tasks/taskHelpers';
import {TaskStateBanner} from 'src/app/tasks/TaskStateBanner';
import {ViewLinkedWorkflowSection} from 'src/app/workflows/ViewLinkedWorkflowSection';
import {TableBodyRender} from 'src/design/molecules/TableBodyRender';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {useAuditLogPermissions} from 'src/shared/auth/permissions/useAuditLogPermissions';
import {useTaskPermissions} from 'src/shared/auth/permissions/useTaskPermissions';
import {useExtensionTabs} from 'src/shared/hooks/useExtensionTabs';
import {useTaskBreadcrumbs} from 'src/shared/hooks/useTaskBreadcrumbs';
import {useUrlQueryTabV2} from 'src/shared/hooks/useUrlQueryTabV2';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import TaskContents from './components/TaskContents';
import TaskDetails from './components/TaskDetails';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

type ViewType =
  | 'content'
  | 'details'
  | 'assign'
  | 'notes'
  | 'notifications'
  | 'attachments'
  | 'audit'
  | '';

export interface ViewTaskPageProps {
  taskType: TaskTypeEnum;
  featureFlag: FeatureFlagEnum;
  module: ModuleEnum;
}

const ViewTaskPage: React.FC<ViewTaskPageProps> = ({
  taskType,
  featureFlag,
  module,
}) => {
  const navigate = useAppNavigate();
  const {setAlert} = useAlert();

  const [confirmDeleteOpened, confirmDeleteHandlers] = useDisclosure(false);

  const {taskId, view} = useParams();
  const [viewItem, setViewItem] = useState<TaskDefinitionResponse>();

  const [isLoading, setIsLoading] = useState<boolean>(true);

  const taskPerms = useTaskPermissions(module);
  const auditPerms = useAuditLogPermissions();

  useSetDocumentTitle(capitalize(GetUrlSectionFromTaskType(taskType)));

  const [triggerGetDetails] = useLazyGetTaskDefinitionDetailsQuery();
  const [deleteTaskDefinition] = useDeleteTaskDefinitionMutation();

  const getData = async () => {
    if (taskId === undefined || isNaN(+taskId)) {
      return;
    }

    // Only block the page on the initial load; refreshes (unlock, transition)
    // must not unmount the content — remounting LockGuard resets its
    // transition tracking and re-triggers effects.
    if (!viewItem) {
      setIsLoading(true);
    }

    try {
      const response = await triggerGetDetails(+taskId).unwrap();
      setViewItem(response);
    } catch (error: any) {
      setAlert(parseErrorMessage(error), 'error');
    }
    setIsLoading(false);
  };

  useEffect(() => {
    void getData();
  }, [taskId]);

  const handleDelete = async () => {
    confirmDeleteHandlers.close();

    try {
      if (!taskId) {
        setAlert('ID not found.', 'error');
      }

      setIsLoading(true);

      await deleteTaskDefinition(+taskId!).unwrap();

      setAlert(
        'Item was successfully deleted. This change may take a few minutes to appear.',
        'success'
      );

      navigate(PathUtility.TasksDashboard(taskType, 'manage'));
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
    setIsLoading(false);
  };

  const breadcrumbs = useTaskBreadcrumbs({
    taskType,
    mode: 'view',
    taskId,
    itemSubject: viewItem?.subject,
  });

  const subText = viewItem ? taskRowSubText(viewItem) : '';

  const pills = viewItem
    ? TaskPillConfig(
        viewItem.state,
        viewItem.taskType,
        viewItem.participation,
        viewItem.approvalState
      )
    : [];

  const canEdit =
    viewItem?.state === TaskDefinitionStateEnum.Draft &&
    taskPerms.update &&
    viewItem?.locked !== true;
  const canDelete = taskPerms.delete && viewItem?.locked !== true;

  const resolvedModule =
    taskType === TaskTypeEnum.ToolboxTalk
      ? ModuleEnum.ToolboxTalks
      : taskType === TaskTypeEnum.Form
      ? ModuleEnum.AssignedForms
      : undefined;

  const extensionTabs = useExtensionTabs(
    resolvedModule ?? ModuleEnum.Invalid,
    taskId ? +taskId : 0
  );

  const {RenderedTabsComponent} = useUrlQueryTabV2<ViewType>({
    view: (view ?? '') as ViewType,
    defaultView: 'content',
    tabs: [
      {
        view: 'content',
        label: 'Content',
        component: (
          <SplitPane
            pane={
              <ViewLinkedWorkflowSection taskDefinitionId={viewItem?.id} />
            }>
            <TaskContents
              module={module}
              item={viewItem}
              onRefreshTransition={getData}
            />
          </SplitPane>
        ),
      },
      {
        view: 'assign',
        label: 'Assign',
        disabled: viewItem?.state === TaskDefinitionStateEnum.Draft,
        component: (
          <>
            <AdminTaskAssignmentsListByTask
              item={viewItem!}
              onTransition={getData}
              module={module}
            />
          </>
        ),
      },
      {
        view: 'notes',
        label: 'Notes',
        disabled: !taskPerms.notes,
        component: (
          <>
            <NotesModule
              entityId={viewItem?.id}
              noteType={NoteModuleEnum.TaskDefinition}
            />
          </>
        ),
      },
      {
        view: 'attachments',
        label: 'Attachments',
        disabled: !taskPerms.attachments,
        component: (
          <>
            {viewItem?.id && (
              <AttachmentsModule
                entityId={viewItem.id}
                attachmentType={AttachmentModuleEnum.TaskDefinition}
              />
            )}
          </>
        ),
      },
      {
        view: 'notifications',
        label: 'Notifications',
        disabled: !taskPerms.notifications,
        component: (
          <>
            <NotificationsModule
              entityId={viewItem?.id}
              notificationType={NotificationModuleEnum.TaskDefinition}
            />
          </>
        ),
      },
      {
        view: 'audit',
        label: 'Audit',
        disabled: !auditPerms.view,
        component: viewItem?.id && (
          <AuditModule
            entityId={String(viewItem.id)}
            entityType={EntityIdentifierEnum.TaskDefinition}
          />
        ),
      },
      {
        view: 'details',
        label: 'Details',
        component: (
          <>
            <TaskDetails item={viewItem} />
          </>
        ),
      },
      ...(resolvedModule ? extensionTabs : []),
    ],
    onSelectView: view =>
      navigate(PathUtility.TaskView(taskType, taskId as string, view as any), {
        replace: true,
      }),
  });

  return (
    <ContentWrapper featureFlag={featureFlag}>
      {!isLoading && viewItem ? (
        <Stack>
          <BackBar
            fallbackTo={PathUtility.TasksDashboard(taskType, 'manage')}
            breadcrumbs={breadcrumbs}
          />
          <PageHeader>
            <PageHeader.Title>
              {viewItem ? viewItem.subject : 'Item not found'}
            </PageHeader.Title>
            {pills.length > 0 && (
              <PageHeader.Badges>
                {pills.map((pill, index) => (
                  <PageHeader.Badge
                    key={index}
                    color={pill.color}
                    variant="light">
                    {pill.text}
                  </PageHeader.Badge>
                ))}
              </PageHeader.Badges>
            )}
            {subText && <PageHeader.Meta>{subText}</PageHeader.Meta>}
            <PageHeader.Actions>
              {taskPerms.create && (
                <PageHeader.Action
                  variant="clone"
                  onClick={() =>
                    navigate(PathUtility.TaskClone(taskType, taskId as string))
                  }>
                  Clone
                </PageHeader.Action>
              )}
              {canEdit && (
                <PageHeader.Action
                  variant="edit"
                  onClick={() =>
                    navigate(PathUtility.TaskEdit(taskType, taskId as string))
                  }>
                  Edit
                </PageHeader.Action>
              )}
              {canDelete && (
                <PageHeader.Action
                  variant="delete"
                  onClick={() => confirmDeleteHandlers.open()}>
                  Delete
                </PageHeader.Action>
              )}
            </PageHeader.Actions>
          </PageHeader>

          <TaskStateBanner module={module} state={viewItem.state} />

          <LockGuard
            id={viewItem.id}
            type={EntityIdentifierEnum.TaskDefinition}
            lockCache={viewItem.locked}
            onUnlock={getData}
          />

          {RenderedTabsComponent}

          <ConfirmDialog
            opened={confirmDeleteOpened}
            onClose={() => confirmDeleteHandlers.close()}
            onConfirm={handleDelete}
            confirmationButtonColor="red"
            title="Are you sure you want to delete this task?"
            body="This action cannot be undone. The task will be deleted, including related assignment data and statistics."
          />
        </Stack>
      ) : (
        <TableBodyRender
          isLoading={isLoading}
          hasData={viewItem !== undefined}
        />
      )}
    </ContentWrapper>
  );
};

// Register the routes with the new system
[
  {
    type: TaskTypeEnum.Approval,
    featureFlag: FeatureFlagEnum.Approvals,
    module: ModuleEnum.Approvals,
  },
  {
    type: TaskTypeEnum.ToolboxTalk,
    featureFlag: FeatureFlagEnum.ToolboxTalks,
    module: ModuleEnum.ToolboxTalks,
  },
  {
    type: TaskTypeEnum.Form,
    featureFlag: FeatureFlagEnum.Forms,
    module: ModuleEnum.AssignedForms,
  },
].forEach(item => {
  routeRegistry.registerPageRoute({
    backNavigation: 'page',
    path: PathUtility.TaskView(item.type, ':taskId', ':view'),
    element: (
      <ViewTaskPage
        taskType={item.type}
        featureFlag={item.featureFlag}
        module={item.module}
      />
    ),
    module: item.module,
    operation: PermissionOperationEnum.View,
    isPrivate: true,
  });
});

export default ViewTaskPage;
